import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { generateEventSlug } from '../utils/eventUtils';
import makeMarkdownComponents from '../utils/markdownComponents';
import { themes } from '../config/themes';

const EventListView = (props) => {
  const theme = props.theme || themes.light;

  const [expandedEventId, setExpandedEventId] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [groupedEvents, setGroupedEvents] = useState({});
  const [isCurrentMonth, setIsCurrentMonth] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);

  useEffect(() => {
    setIsCurrentMonth(false);

    if (!props.events || props.events.length === 0) {
      setIsCurrentMonth(false);
      return;
    }

    let filtered = props.events;

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var curMonth = props.curMonth;
    var curYear = props.curYear;
    var isCurrentMonth = (today.getMonth() + 1 === curMonth) && (today.getFullYear() === curYear);
    setIsCurrentMonth(isCurrentMonth);

    if (isCurrentMonth && !showPastEvents) {
      filtered = filtered.filter(event => {
        const eventDate = new Date(event.endDate || event.startDate);
        return eventDate >= today;
      });
    }

    setFilteredEvents(filtered);

    const grouped = {};
    filtered.forEach(event => {
      const date = new Date(event.startDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });

    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => new Date(a) - new Date(b))
      .forEach(date => {
        sortedGrouped[date] = grouped[date];
      });

    setGroupedEvents(sortedGrouped);
  }, [props.events, showPastEvents]);

  const toggleEventDetails = (eventId) => {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  };

  const togglePastEvents = () => {
    setShowPastEvents(prevState => !prevState);
  };

  return (
    <div className={`rounded-lg shadow-md opacity-[.93] ${theme.gridBg} p-4 w-full h-full overflow-y-auto`} role="region" aria-label="Event List">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 mt-4 gap-4">
        {isCurrentMonth && (
          <button
            onClick={togglePastEvents}
            className={`${theme.actionButton} py-2 px-4 rounded transition flex items-center self-start sm:self-center`}
            aria-pressed={showPastEvents}
            aria-label={showPastEvents ? "Hide past events" : "Show past events"}
          >
            <span className="mr-2">{showPastEvents ? "Hide" : "Show"} Past Events</span>
            <i className={`fas fa-${showPastEvents ? "eye-slash" : "eye"}`} aria-hidden="true"></i>
          </button>
        )}
      </div>

      {props.events.length === 0 ? (
        <div className={`${theme.eventBg} rounded-lg p-8 text-center`} role="status" aria-live="polite">
          <div className="flex flex-col items-center">
            <i className={`fas fa-calendar-times ${theme.link} text-4xl mb-4`} aria-hidden="true"></i>
            <h3 className={`text-xl font-bold ${theme.text} mb-2`}>No Events Found</h3>
            <p className={theme.secondaryText}>There are no events added for this month.</p>
          </div>
        </div>
      ) : Object.keys(groupedEvents).length === 0 ? (
        <div className={`${theme.eventBg} rounded-lg p-8 text-center`} role="status" aria-live="polite">
          <div className="flex flex-col items-center">
            <i className={`fas fa-filter ${theme.link} text-4xl mb-4`} aria-hidden="true"></i>
            <h3 className={`text-xl font-bold ${theme.text} mb-2`}>No Matching Events</h3>
            <p className={theme.secondaryText}>No events match your selected filter.</p>
          </div>
        </div>
      ) : (
        <div role="list">
          {Object.entries(groupedEvents).map(([date, dateEvents]) => (
            <div key={date} className={`mb-6 border-b ${theme.border} pb-2`} role="listitem">
              <h3 className={`text-lg font-bold ${theme.link} mb-2`}>{date}</h3>

              <div className="space-y-3" role="list">
                {dateEvents.map(event => (
                  <div key={event.id} className={`${theme.cardBg} ${theme.text} rounded-lg shadow-sm p-3 items-center ${
                    event.eventType === 'Featured' ? `border-4 border-yellow-400 ${theme.featuredDay}` : ''
                  }`} role="listitem">
                    <div className="flex justify-between items-start">
                      {event.sponsorLogo && (
                        <div className="mr-2 flex items-center">
                          <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Sponsor'} className="max-h-8" />
                        </div>
                      )}
                      {event.eventType === 'Featured' && (
                        <div className="bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-full">Featured</div>
                      )}

                      <div className="flex-1">
                        <h4 className={`font-bold ${theme.text}`}>{event.title}</h4>
                        <p className={`text-sm ${theme.secondaryText}`}>{event.organization}</p>
                        {(new Date(event.startDate).getHours() !== 0 || new Date(event.startDate).getMinutes() !== 0) && (
                          <p className={`text-sm font-semibold ${theme.text}`}>
                            {new Date(event.startDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} -&nbsp;
                            {new Date(event.endDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => toggleEventDetails(event.id)}
                        className={`${theme.expandButton} font-bold py-1 px-3 rounded-full transition`}
                        aria-expanded={expandedEventId === event.id}
                        aria-label={expandedEventId === event.id ? "Collapse details for " + event.title : "Expand details for " + event.title}
                      >
                        {expandedEventId === event.id ? '−' : '+'}
                      </button>
                    </div>

                    {event.tags && event.tags.length > 0 && (
                      <div className="mb-4 text-center">
                        <div className="flex flex-wrap gap-2 mt-2 justify-center">
                          {event.tags.map((tag, index) => (
                            <span
                              key={index}
                              className={`${theme.tag} text-xs font-medium px-2.5 py-0.5 rounded`}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {window.location.hash.includes('#email') && (
                      <div className="inline-block text-center py-3">
                        <a
                          href={`${window.location.origin}/#${generateEventSlug(event)}`}
                          className={`${theme.link} text-sm flex items-center gap-2 text-center`}
                        >
                          View Details
                        </a>
                      </div>
                    )}

                    {expandedEventId === event.id && (
                      <div>
                        <div className={`mt-3 pt-3 border-t ${theme.border}`} role="region" aria-label={`Details for ${event.title}`}>
                          <div className="mb-3">
                            <ReactMarkdown components={makeMarkdownComponents(theme)}>{event.description}</ReactMarkdown>
                          </div>
                          {event.eventURL && new Date(event.endDate || event.startDate) >= new Date(new Date().setHours(0, 0, 0, 0)) && (
                            <a
                              href={event.eventURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-block ${theme.actionButton} py-2 px-4 rounded transition`}
                              aria-label={`Go to event page for ${event.title}`}
                            >
                              {event.eventURL === '/private-events' ? 'About private events' : 'Go to event page'}
                            </a>
                          )}
                        </div>
                        <div className="inline-block text-center py-3">
                          <button
                            onClick={() => {
                              const slug = generateEventSlug(event);
                              const url = `${window.location.origin}/#${slug}`;
                              navigator.clipboard.writeText(url);
                            }}
                            className={`${theme.link} text-sm flex items-center gap-2 text-center`}
                          >
                            <i className="fas fa-share-alt text-center"></i>
                            Share This (Copy to Clipboard)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventListView;
